// Phase 1A Phase 9C.2 — Icon master registry.
//
// Imports every namespace icon file + the literal UI-action icons,
// then exposes a single typed map keyed by IconKey. The
// `satisfies Record<IconKey, IconDef>` assertion proves at compile
// time that every IconKey has a registered definition; if a future
// addition forgets a key, TypeScript will fail the build.
//
// Renderer in Icon.tsx imports getIcon() from this file. Tests
// import ICONS to enumerate the registry.

import { DATA_TIER_ICONS } from "./data-tier-icons.ts";
import { DECISION_ICONS } from "./decision-icons.ts";
import { DOMAIN_ICONS } from "./domain-icons.ts";
import type { IconDef, IconKey } from "./icon-types.ts";
import { LIFECYCLE_ICONS } from "./lifecycle-icons.ts";
import { MODE_ICONS } from "./mode-icons.ts";
import { PROCESS_ICONS } from "./process-icons.ts";
import { READINESS_ICONS } from "./readiness-icons.ts";
import { RESERVE_ICONS } from "./reserve-icons.ts";
import { RISK_ICONS } from "./risk-icons.ts";

// ─── Literal UI action icons ─────────────────────────────────
//
// Six actions used by toolbars / CTAs / nav. Geometric only.

const ACTION_ICONS: Record<string, IconDef> = {
  "action.search": {
    key: "action.search",
    shapes: [
      { type: "circle", cx: 11, cy: 11, r: 6 },
      { type: "line", x1: 15, y1: 15, x2: 19, y2: 19 },
    ],
  },
  "action.filter": {
    key: "action.filter",
    shapes: [{ type: "polyline", points: "4,6 20,6 14,13 14,19 10,17 10,13" }],
  },
  "action.sort": {
    key: "action.sort",
    shapes: [
      { type: "line", x1: 5, y1: 8, x2: 19, y2: 8 },
      { type: "line", x1: 7, y1: 12, x2: 17, y2: 12 },
      { type: "line", x1: 9, y1: 16, x2: 15, y2: 16 },
    ],
  },
  "action.plus": {
    key: "action.plus",
    shapes: [
      { type: "line", x1: 12, y1: 5, x2: 12, y2: 19, strokeWidth: 2.2 },
      { type: "line", x1: 5, y1: 12, x2: 19, y2: 12, strokeWidth: 2.2 },
    ],
  },
  "action.trash": {
    key: "action.trash",
    shapes: [
      { type: "polyline", points: "5,7 19,7" },
      { type: "path", d: "M9 7 V5 H15 V7" },
      { type: "path", d: "M7 7 L8 19 H16 L17 7" },
      { type: "line", x1: 11, y1: 10, x2: 11, y2: 16 },
      { type: "line", x1: 13, y1: 10, x2: 13, y2: 16 },
    ],
  },
  "action.chevron_right": {
    key: "action.chevron_right",
    shapes: [{ type: "polyline", points: "9,5 16,12 9,19" }],
  },
};

// ─── Master registry ─────────────────────────────────────────

const MERGED: Record<string, IconDef> = {
  ...DOMAIN_ICONS,
  ...MODE_ICONS,
  ...READINESS_ICONS,
  ...RISK_ICONS,
  ...RESERVE_ICONS,
  ...PROCESS_ICONS,
  ...DECISION_ICONS,
  ...DATA_TIER_ICONS,
  ...LIFECYCLE_ICONS,
  ...ACTION_ICONS,
};

// `satisfies` proves at compile time that every IconKey is present.
export const ICONS = MERGED satisfies Record<IconKey, IconDef>;

export function getIcon(name: IconKey): IconDef {
  const def = ICONS[name];
  if (!def) throw new Error(`icons: unknown IconKey "${String(name)}"`);
  return def;
}

export function hasIcon(name: string): name is IconKey {
  return name in ICONS;
}
